/**
 * DOMWalker - Analyzes DOM content and creates readable segments
 * Implements intelligent content discovery and segmentation for text-to-speech functionality
 */
export default class DOMWalker {
	/**
	 * Node types constants
	 */
	static ELEMENT_NODE = 1;
	static TEXT_NODE = 3;

	/** @type {Object} Current configuration */
	#config;

	/**
	 * Initialize DOMWalker with configuration
	 * @param {Object} config - Configuration options
	 * @param {string} config.contentSelector - CSS selector for content container
	 * @param {Array} config.segmentSelectors - Array of selectors for segment elements
	 * @param {Array} config.excludedElements - Array of elements to exclude
	 * @param {boolean} config.respectAria - Whether to respect ARIA attributes
	 */
	constructor(config = {}) {
		// Store the provided configuration with respectAria default
		this.#config = {
			respectAria: true,
			...config,
		};

		// Validate required configuration
		if (
			!config.segmentSelectors ||
			!Array.isArray(config.segmentSelectors)
		) {
			throw new Error(
				"DOMWalker: segmentSelectors must be provided as an array",
			);
		}
		if (
			!config.excludedElements ||
			!Array.isArray(config.excludedElements)
		) {
			throw new Error(
				"DOMWalker: excludedElements must be provided as an array",
			);
		}
	}

	/**
	 * Main method to analyze page content and return segments
	 * @returns {Array} Array of segment objects
	 */
	analyzeContent() {
		const contentContainer = this.findContentContainer();

		if (!contentContainer) {
			console.warn("TitusJS: No suitable content container found");
			return [];
		}

		const segments = this.extractSegments(contentContainer);

		return segments;
	}

	/**
	 * Find the best content container based on configuration and heuristics
	 * @returns {Element|null} The content container element
	 */
	findContentContainer() {
		// Priority 1: User-specified selector
		if (this.#config.contentSelector) {
			const userContainer = document.querySelector(
				this.#config.contentSelector,
			);
			if (userContainer) {
				return userContainer;
			}
			console.warn(
				`TitusJS: Specified content selector "${this.#config.contentSelector}" not found, falling back to automatic detection`,
			);
		}

		// Priority 2: Look for <main> element
		const mainElement = document.querySelector("main");
		if (mainElement) {
			return mainElement;
		}

		// Priority 3: Look for first <article> element
		const articleElement = document.querySelector("article");
		if (articleElement) {
			return articleElement;
		}

		// Fallback: Use body but create a virtual container excluding unwanted elements
		return this.createFilteredBodyContainer();
	}

	/**
	 * Create a virtual container from body content, excluding unwanted elements
	 * @returns {DocumentFragment} Fragment containing filtered content
	 */
	createFilteredBodyContainer() {
		const fragment = document.createDocumentFragment();
		const bodyChildren = Array.from(document.body.children);

		bodyChildren.forEach((child) => {
			// Skip excluded elements
			if (
				this.#config.excludedElements.includes(
					child.tagName.toLowerCase(),
				)
			) {
				return;
			}

			// Clone the element to avoid modifying the original DOM
			const clonedChild = child.cloneNode(true);
			fragment.appendChild(clonedChild);
		});

		return fragment;
	}

	/**
	 * Extract readable segments from a container
	 * @param {Element|DocumentFragment} container - Container to analyze
	 * @returns {Array} Array of segment objects
	 */
	extractSegments(container) {
		const segments = [];
		const segmentElements = this.findSegmentElements(container);

		segmentElements.forEach((element, index) => {
			const segment = this.createSegment(element, index);
			if (segment && segment.text.trim().length > 0) {
				segments.push(segment);
			}
		});

		return segments;
	}

	/**
	 * Find all elements that should be treated as individual segments
	 * @param {Element|DocumentFragment} container - Container to search
	 * @returns {Array} Array of DOM elements
	 */
	findSegmentElements(container) {
		const selector = this.#config.segmentSelectors.join(", ");
		const elements = container.querySelectorAll
			? Array.from(container.querySelectorAll(selector))
			: Array.from(container.childNodes).filter(
					(node) =>
						node.nodeType === DOMWalker.ELEMENT_NODE &&
						this.#config.segmentSelectors.includes(
							node.tagName.toLowerCase(),
						),
				);

		// Filter out nested segments to prevent duplicates
		const filteredElements = elements.filter((element) => {
			if (!this.isElementReadable(element)) {
				return false;
			}

			// Check if this element is nested inside another selected segment
			return !this.hasSegmentAncestor(element, elements);
		});

		return filteredElements;
	}

	/**
	 * Check if an element has an ancestor that is also a segment
	 * @param {Element} element - Element to check
	 * @param {Array} allSegments - Array of all potential segment elements
	 * @returns {boolean} Whether element has a segment ancestor
	 */
	hasSegmentAncestor(element, allSegments) {
		let parent = element.parentElement;

		while (parent) {
			// If we find a parent that's also in our segments list, this element is nested
			if (allSegments.includes(parent)) {
				return true;
			}
			parent = parent.parentElement;
		}

		return false;
	}

	/**
	 * Check if an element should be read (visibility, ARIA attributes, etc.)
	 * @param {Element} element - Element to check
	 * @returns {boolean} Whether the element is readable
	 */
	isElementReadable(element) {
		// Check visibility
		if (!this.isElementVisible(element)) {
			return false;
		}

		// Check ARIA attributes if enabled
		if (this.#config.respectAria) {
			if (element.getAttribute("aria-hidden") === "true") {
				return false;
			}
		}

		// Check if element has meaningful text content
		const textContent = this.extractTextContent(element);
		return textContent.trim().length > 0;
	}

	/**
	 * Check if element is visible (not hidden by CSS)
	 * @param {Element} element - Element to check
	 * @returns {boolean} Whether element is visible
	 */
	isElementVisible(element) {
		const style = window.getComputedStyle(element);

		return !(
			style.display === "none" ||
			style.visibility === "hidden" ||
			style.opacity === "0" ||
			element.hidden
		);
	}

	/**
	 * Extract clean text content from an element
	 * @param {Element} element - Element to extract text from
	 * @returns {string} Clean text content
	 */
	extractTextContent(element) {
		// Prefer aria-label if available
		const ariaLabel = element.getAttribute("aria-label");
		if (ariaLabel && ariaLabel.trim()) {
			return ariaLabel.trim();
		}

		// Handle aria-labelledby
		const ariaLabelledBy = element.getAttribute("aria-labelledby");
		if (ariaLabelledBy) {
			const labelElement = document.getElementById(ariaLabelledBy);
			if (labelElement) {
				return labelElement.textContent.trim();
			}
		}

		// Extract regular text content, excluding hidden elements
		return this.getCleanTextContent(element);
	}

	/**
	 * Get clean text content, excluding text from hidden or excluded elements
	 * @param {Element} element - Element to process
	 * @returns {string} Clean text content
	 */
	getCleanTextContent(element) {
		let text = "";

		const walker = document.createTreeWalker(
			element,
			NodeFilter.SHOW_TEXT,
			{
				acceptNode: (node) => {
					const parentElement = node.parentElement;

					// Skip if parent is hidden or excluded
					if (!this.isElementVisible(parentElement)) {
						return NodeFilter.FILTER_REJECT;
					}

					// Skip script/style content
					const tagName = parentElement.tagName.toLowerCase();
					if (["script", "style", "noscript"].includes(tagName)) {
						return NodeFilter.FILTER_REJECT;
					}

					return NodeFilter.FILTER_ACCEPT;
				},
			},
		);

		let textNode = walker.nextNode();
		while (textNode) {
			const nodeText = textNode.textContent.trim();
			if (nodeText) {
				text += (text ? " " : "") + nodeText;
			}
			textNode = walker.nextNode();
		}

		return text;
	}

	/**
	 * Create a segment object from a DOM element
	 * @param {Element} element - Source element
	 * @param {number} index - Segment index
	 * @returns {Object|null} Segment object or null if invalid
	 */
	createSegment(element, index) {
		const text = this.extractTextContent(element);

		if (!text.trim()) {
			return null;
		}

		return {
			id: `tjs-segment-${index}`,
			index: index,
			element: element,
			text: text.trim(),
			tagName: element.tagName.toLowerCase(),
			isHeading: /^h[1-6]$/.test(element.tagName.toLowerCase()),
		};
	}

	/**
	 * Re-analyze content (useful for dynamically loaded content)
	 * @returns {Array} Updated array of segments
	 */
	rescanContent() {
		return this.analyzeContent();
	}
}
